// apps/web/src/pages/dashboard/MyVehicles.tsx

import React, { useState, useCallback } from 'react';
import { trpc } from '@/trpc/client';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useDropzone, FileRejection } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

import { CreateVehicleSchema, TCreateVehicleSchema } from '@/lib/validators/vehicle';
import CompanyVerificationModal from '@/components/dashboard/CompanyVerificationModal';

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogClose,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  Loader2,
  Truck,
  PlusCircle,
  AlertTriangle,
  BadgeCheck,
  UploadCloud,
  X,
  FileText,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// Helper function to resolve image paths
function resolveSrc(path?: string) {
  if (!path) return '/images/placeholder.png';
  return path.startsWith('http') ? path : `${apiUrl}/uploads/${path}`;
}

// --- VehicleCard with carousel ---
interface ImageType { filePath: string; }
interface DocType { id: string; filePath: string; type: string; }
interface VehicleType {
  id: string;
  make: string;
  model: string;
  licensePlate: string;
  capacityKg: number;
  verified: boolean;
  images: ImageType[];
  documents: DocType[];
}

const VehicleCard: React.FC<{ vehicle: VehicleType }> = ({ vehicle }) => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const openAt = (i: number) => {
    setIndex(i);
    setOpen(true);
  };
  const prev = () => setIndex((index - 1 + vehicle.images.length) % vehicle.images.length);
  const next = () => setIndex((index + 1) % vehicle.images.length);

  return (
    <div className="rounded-lg border bg-background overflow-hidden">
      {/* Cover + thumbnails */}
      <div className="relative cursor-pointer" onClick={() => openAt(0)}>
        <img
          src={resolveSrc(vehicle.images[0]?.filePath)}
          alt={`${vehicle.make} ${vehicle.model}`}
          className="w-full h-40 sm:h-56 object-cover"
        />
        {vehicle.images.length > 1 && (
          <div className="absolute bottom-2 left-2 flex space-x-2 overflow-x-auto">
            {vehicle.images.map((img, i) => (
              <img
                key={i}
                src={resolveSrc(img.filePath)}
                alt={`Thumb ${i + 1}`}
                className="w-12 h-12 object-cover rounded border-2 border-white"
                onClick={e => { e.stopPropagation(); openAt(i); }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Full-screen carousel */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 bg-black/80 flex items-center justify-center">
          <DialogTitle className="sr-only">Vehicle Image Carousel</DialogTitle>
          <div className="relative">
            <img
              src={resolveSrc(vehicle.images[index].filePath)}
              alt={`Slide ${index + 1}`}
              className="max-h-[80vh] max-w-screen-lg object-contain"
            />
            <DialogClose asChild>
              <button className="absolute top-4 right-4 p-1 rounded-full bg-white/20 text-white">
                <X size={24} />
              </button>
            </DialogClose>
            {vehicle.images.length > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute top-1/2 left-4 -translate-y-1/2 p-2 rounded-full bg-white/20 text-white"
                >
                  <ChevronLeft size={32} />
                </button>
                <button
                  onClick={next}
                  className="absolute top-1/2 right-4 -translate-y-1/2 p-2 rounded-full bg-white/20 text-white"
                >
                  <ChevronRight size={32} />
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className="p-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold">{vehicle.make} {vehicle.model}</h3>
          {vehicle.verified
            ? <BadgeCheck className="text-green-600" />
            : <AlertTriangle className="text-orange-500" />}
        </div>
        <p className="text-sm text-muted-foreground font-mono">
          {vehicle.licensePlate} | {vehicle.capacityKg} kg
        </p>
      </div>
    </div>
  );
};

// --- MyVehiclesList using VehicleCard ---
const MyVehiclesList: React.FC = () => {
  const { data: vehicles, isLoading } = trpc.vehicle.getMyVehicles.useQuery();

  // Group by Batch ID
  const groupedVehicles = React.useMemo(() => {
    if (!vehicles) return [];

    const groups: Record<string, VehicleType[]> = {};
    const singles: VehicleType[] = [];

    vehicles.forEach(v => {
      // FIX: Typescript might not know bulkBatchId exists yet if we don't cast or if types are stale
      // We'll cast to any for now to avoid build break if types are lagging
      const batchId = (v as any).bulkBatchId;
      if (batchId) {
        if (!groups[batchId]) groups[batchId] = [];
        groups[batchId].push(v);
      } else {
        singles.push(v);
      }
    });

    const batchCards: VehicleType[] = Object.values(groups).map(group => {
      const first = group[0];
      return {
        ...first,
        // Hack: overload Make/Model to show count
        model: `${first.model.split('#')[0].trim()} (Batch of ${group.length})`,
        // Use the batch ID as key if needed, or just keep first ID
      };
    });

    return [...batchCards, ...singles].sort((a, b) =>
      new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
  }, [vehicles]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!vehicles?.length) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <h3 className="text-lg font-semibold">No vehicles registered yet.</h3>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {groupedVehicles.map((v) => (
        <VehicleCard key={v.id} vehicle={v} />
      ))}
    </div>
  );
};

// --- FileDropzone & AddVehicleForm (unchanged) ---
const FileDropzone = ({ field, label, maxFiles = 1 }: { field: { value: File[]; onChange: (files: File[] | undefined) => void }; label: string; maxFiles?: number }) => {
  const onDrop = useCallback((accepted: File[], rejections: FileRejection[]) => {
    if (rejections.length) {
      toast.error(rejections[0].errors[0].message);
      return;
    }
    const current = field.value || [];
    const combined = maxFiles === 1 ? accepted : current.concat(accepted);
    if (combined.length > maxFiles) {
      toast.error(`You can only upload up to ${maxFiles} file(s).`);
      field.onChange(combined.slice(0, maxFiles));
    } else {
      field.onChange(combined);
    }
  }, [field, maxFiles]);


  const removeFile = (i: number) => {
    const updated = (field.value || []).filter((_: File, idx: number) => idx !== i);
    field.onChange(updated.length ? updated : undefined);
  };

  const files: File[] = field.value || [];
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: maxFiles > 1,
    maxFiles,
    accept: { 'image/*': ['.jpeg', '.png', '.webp'], 'application/pdf': ['.pdf'] },
  });

  return (
    <div className="space-y-2">
      <FormLabel>{label}</FormLabel>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary ${isDragActive ? 'border-primary bg-primary/10' : ''}`}
      >
        <input {...getInputProps()} />
        <UploadCloud className="mx-auto w-8 h-8 text-muted-foreground" />
        <p className="font-semibold">
          {isDragActive ? 'Drop files here...' : 'Drag & drop files, or click to select'}
        </p>
      </div>
      {files.length > 0 && (
        <div className="mt-2 space-y-2">
          {files.map((file, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded-md text-sm">
              <span className="truncate">{file.name}</span>
              <Button variant="ghost" size="icon" onClick={() => removeFile(idx)}><X size={16} /></Button>
            </div>
          ))}
        </div>
      )}
      <FormMessage />
    </div>
  );
};

const AddVehicleForm: React.FC<{ onVehicleAdded: () => void }> = ({ onVehicleAdded }) => {
  const { token, user } = useAuthStore();
  const form = useForm<TCreateVehicleSchema>({
    resolver: zodResolver(CreateVehicleSchema),
    defaultValues: { galleryImages: [] }
  });
  const utils = trpc.useUtils();
  const createVehicle = trpc.vehicle.create.useMutation({
    onSuccess: () => {
      toast.success('Vehicle submitted for verification!');
      form.reset();
      onVehicleAdded();
      utils.vehicle.getMyVehicles.invalidate();
      setUploadProgress(0);
    },
    onError: (err) => toast.error(err.message)
  });

  const [uploadProgress, setUploadProgress] = useState(0);

  // Force boolean check with case-insensitivity
  const isFleetManager = String(user?.role).toUpperCase() === 'FLEET_MANAGER';

  // Helper to calculate total size
  const getTotalSize = (files: File[]) => files.reduce((acc, f) => acc + f.size, 0);

  const uploadFiles = async (
    files: File[],
    type: string,
    onProgress: (loaded: number, total: number) => void
  ): Promise<string[]> => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    fd.append('type', type);

    const { data } = await axios.post(`${apiUrl}/upload`, fd, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          onProgress(progressEvent.loaded, progressEvent.total);
        }
      }
    });

    return data.files.map((f: { filePath: string }) => f.filePath);
  };

  const onSubmit = async (data: TCreateVehicleSchema) => {
    try {
      setUploadProgress(1); // Start progress indicating activity

      const galleryFiles = data.galleryImages || [];
      const regFile = data.registrationDocument ? [data.registrationDocument] : [];
      const ownFile = data.ownershipDocument ? [data.ownershipDocument] : [];

      // Calculate total bytes to upload for accurate global progress
      const allFiles = [...galleryFiles, ...regFile, ...ownFile];
      const totalBytes = getTotalSize(allFiles);
      let uploadedBytes = 0;

      // Helper to update global progress
      const updateGlobalProgress = (chunkLoaded: number, chunkSize: number, previousBytes: number) => {
        const currentTotal = previousBytes + chunkLoaded;
        const percentage = Math.round((currentTotal / totalBytes) * 100);
        setUploadProgress(percentage);
      };

      // 1. Upload Gallery
      let gallery: string[] = [];
      if (galleryFiles.length > 0) {
        gallery = await uploadFiles(galleryFiles, 'vehicle_gallery', (loaded) => {
          updateGlobalProgress(loaded, 0, uploadedBytes);
        });
        uploadedBytes += getTotalSize(galleryFiles);
      }

      // 2. Upload Registration Doc (Conditional)
      let reg = '';
      if (!isFleetManager && regFile.length > 0) {
        const res = await uploadFiles(regFile, 'vehicle_documents', (loaded) => {
          updateGlobalProgress(loaded, 0, uploadedBytes);
        });
        reg = res[0];
        uploadedBytes += getTotalSize(regFile);
      }

      // 3. Upload Ownership Doc (Conditional)
      let own = '';
      if (!isFleetManager && ownFile.length > 0) {
        const res = await uploadFiles(ownFile, 'vehicle_documents', (loaded) => {
          updateGlobalProgress(loaded, 0, uploadedBytes);
        });
        own = res[0];
      }

      setUploadProgress(100); // Ensure complete

      createVehicle.mutate({
        make: data.make,
        model: data.model,
        licensePlate: data.licensePlate,
        capacity: data.capacity!,
        capacityUnit: data.capacityUnit!,
        imageFilePaths: gallery,
        registrationDocPath: reg, // Will be empty string if fleet manager, which is allowed by our modified API
        ownershipDocPath: own
      });
    } catch (err) {
      setUploadProgress(0);
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const isLoading = createVehicle.isLoading || (uploadProgress > 0 && uploadProgress < 100);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={form.control} name="make" render={({ field }) => (
            <FormItem>
              <FormLabel>Vehicle Make</FormLabel>
              <FormControl><Input {...field} placeholder="e.g., Scania" /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <FormField
            control={form.control}
            name="model"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Vehicle Model</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., R 450" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* License + Capacity */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField
            control={form.control}
            name="licensePlate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>License Plate</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., BT 1234 AB" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="capacity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Capacity</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="e.g., 20000"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(e.target.valueAsNumber || undefined)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="capacityUnit"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unit</FormLabel>
                <FormControl>
                  <select {...field} className="w-full border rounded p-2">
                    <option value="KG">kg</option>
                    <option value="TONNE">tonne</option>
                    <option value="POUND">lb</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        {/* Gallery Images */}
        <FormField
          control={form.control}
          name="galleryImages"
          render={({ field }) => (
            <FileDropzone field={field} label="Gallery Images (1–5)" maxFiles={5} />
          )}
        />

        {/* Documents - Hide for Fleet Managers */}
        {!isFleetManager && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="registrationDocument"
              render={({ field }) => (
                <FileDropzone
                  field={{
                    ...field,
                    value: field.value ? [field.value] : [],
                    onChange: (files: File[] | undefined) => field.onChange(files?.[0]),
                  }}
                  label="Registration Certificate (PDF/Image)"
                />
              )}
            />
            <FormField
              control={form.control}
              name="ownershipDocument"
              render={({ field }) => (
                <FileDropzone
                  field={{
                    ...field,
                    value: field.value ? [field.value] : [],
                    onChange: (files: File[] | undefined) => field.onChange(files?.[0]),
                  }}
                  label="Proof of Ownership (PDF/Image)"
                />
              )}
            />
          </div>
        )}

        {uploadProgress > 0 && uploadProgress < 100 && (
          <Progress value={uploadProgress} />
        )}

        <div className="flex flex-col sm:flex-row justify-end pt-4 border-t gap-4">
          <Button type="submit" variant="premium" size="lg" disabled={isLoading} className="w-full sm:w-auto">
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PlusCircle className="mr-2 h-4 w-4" />
            )}
            {uploadProgress > 0
              ? `Uploading… (${Math.round(uploadProgress)}%)`
              : "Submit Vehicle for Verification"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

// --- 4) Main MyVehicles page ---
const MyVehicles: React.FC = () => {
  const { user } = useAuthStore();
  const utils = trpc.useUtils();
  const refetchVehicles = () => utils.vehicle.getMyVehicles.invalidate();

  const [showCompanyVerification, setShowCompanyVerification] = useState(false);

  // Check if Fleet Manager needs verification
  const isFleetManager = String(user?.role).toUpperCase() === 'FLEET_MANAGER';
  const needsVerification = isFleetManager && (user.verificationStatus === 'UNVERIFIED' || user.verificationStatus === 'REJECTED');

  if (user?.role !== "TRANSPORTER" && user?.role !== "FLEET_MANAGER") {
    return (
      <div className="container py-12">
        Only transporters can access this page.
      </div>
    );
  }

  return (
    <div className="container mx-auto py-4 px-2 sm:py-12 sm:px-4 animate-fade-in space-y-6 sm:space-y-12">
      <CompanyVerificationModal
        open={showCompanyVerification}
        onOpenChange={setShowCompanyVerification}
        onSuccess={() => utils.user.getProfile.invalidate()}
      />

      <header className="space-y-2 px-2">
        <div className="flex items-center space-x-3">
          <Truck className="w-6 h-6 sm:w-8 sm:h-8" />
          <h1 className="text-2xl sm:text-4xl font-bold">Manage Your Fleet</h1>
        </div>
        <p className="text-muted-foreground text-sm sm:text-lg">
          Add and view your registered vehicles here.
        </p>
      </header>

      {user?.role === 'TRANSPORTER' && user.verificationStatus !== 'VERIFIED' && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">
                Profile Verification in Progress
              </p>
              <p className="text-xs text-muted-foreground">
                You can add your vehicle details now so they can be verified alongside your personal identity documents.
              </p>
            </div>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="text-xs shrink-0"
            >
              <Link to={user.onboardingStatus === 'pending_admin_approval' ? '/onboarding/pending' : '/onboarding/verify'}>
                View Verification Progress
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {needsVerification ? (
        <Card className="border-orange-500/50 bg-orange-500/10">
          <CardHeader>
            <CardTitle className="text-orange-600 flex items-center gap-2">
              <AlertTriangle />
              Company Verification Required
            </CardTitle>
            <CardDescription className="text-orange-700/80">
              To start adding vehicles, you must first verify your company by uploading your Business Registration Certificate.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setShowCompanyVerification(true)} variant="destructive">
              Verify Company Now
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Add a New Vehicle</CardTitle>
            <CardDescription>
              Fill out all details accurately. Your vehicle will be reviewed before activation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AddVehicleForm onVehicleAdded={refetchVehicles} />
          </CardContent>
        </Card>
      )}

      <section>
        <h2 className="text-3xl font-bold mb-4">Your Registered Vehicles</h2>
        <MyVehiclesList />
      </section>
    </div>
  );
};

export default MyVehicles;
